import { useNavigate, useParams } from "react-router-dom";
import { Installation, Integration, ProviderInfo } from "data/api";
import { StatusEnum, statusEnumLookup } from "data/enums";
import { useListIntegrations } from "src/hooks/useListIntegrations";
import { useProviders } from "src/hooks/useProviders";
import { MainPageTemplate } from "src/layout/SidebarLayout/MainPageLayout/MainPageTemplate";
import { capitalizeFirstLetter, formatDateTime } from "src/util";
import { LoadingPage } from "src/views/LoadingPage/LoadingPage";

import { StatusPill } from "components/StatusPill";
import { TableComponent } from "components/TableComponent";
import { Monospace } from "components/Typography/Monospace";

import {
  ToastSuccess,
  useDeleteInstallationMutation,
} from "../../InstallationsPage/useDeleteInstallationMutation";
import { useListAllInstallations } from "../useListAllInstallations";

import { CustomerDetailPanel } from "./CustomerDetailPanel";

type CustomerDetailsContentProps = {
  customerInstallations: Array<Installation>;
  integrations: Array<Integration> | undefined;
  providers: Record<string, ProviderInfo> | undefined;
  projectId: string;
};

const InstallationRow = ({
  installation,
  integration,
  providerDisplayName,
  projectId,
  customerName,
}: {
  installation: Installation;
  integration: Integration | undefined;
  providerDisplayName: string;
  projectId: string;
  customerName: string;
}) => {
  const navigate = useNavigate();
  const { mutate: deleteInstallationMutate } = useDeleteInstallationMutation();

  const onDeleteInstallation = () => {
    if (!installation.id) throw Error("No installationId provided");
    deleteInstallationMutate(
      {
        data: {
          installationId: installation.id,
          integrationId: installation.integrationId,
        },
        toastId: "delete-installation",
      },
      { onSuccess: () => ToastSuccess() }
    );
  };

  const handleRowClick = () => {
    navigate(
      `/projects/${projectId}/integrations/${installation.integrationId}/installations/${installation.id}`
    );
  };

  return (
    <TableComponent.TableRowClickable
      key={installation.id}
      onRowClick={handleRowClick}
      ariaLabel={`View installation ${installation.id} details`}
    >
      <TableComponent.TableCell>{providerDisplayName}</TableComponent.TableCell>
      <TableComponent.TableCell>
        <Monospace>{integration?.name || integration?.id}</Monospace>
      </TableComponent.TableCell>
      <TableComponent.TableCell>
        <Monospace>{installation.id}</Monospace>
      </TableComponent.TableCell>
      <TableComponent.TableCell>
        <StatusPill status={statusEnumLookup[installation.lastOperationStatus ?? ''] ?? StatusEnum.UNKNOWN} />
      </TableComponent.TableCell>
      <TableComponent.TableCell>
        <Monospace>{formatDateTime(installation.createTime)}</Monospace>
      </TableComponent.TableCell>
      <TableComponent.TableCell onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <TableComponent.OverFlowCell
          actions={[
            {
              name: "Delete installation",
              action: onDeleteInstallation,
              variant: "danger",
              hasDialog: true,
              dialogVariant: "danger",
              dialogConfirmText: "Delete Installation",
              dialogChildren: (
                <p>
                  Once you delete the installation for <b>{customerName}</b>, it
                  will no longer work for that customer and any scheduled reads
                  will stop.
                </p>
              ),
            },
          ]}
        />
      </TableComponent.TableCell>
    </TableComponent.TableRowClickable>
  );
};

const CustomerDetailsContent = ({
  customerInstallations,
  integrations,
  providers,
  projectId,
}: CustomerDetailsContentProps) => {
  if (!customerInstallations.length) {
    return (
      <div className="p-4 text-center text-gray-600">
        No installations found for this customer.
      </div>
    );
  }

  const customerName =
    customerInstallations[0]?.group?.groupName || "this customer";
  const groupRef = customerInstallations[0]?.group?.groupRef || "";

  return (
    <>
      <CustomerDetailPanel
        customerName={customerName}
        groupRef={groupRef}
        customerInstallations={customerInstallations}
      />
      <TableComponent.Table>
        <TableComponent.TableHeader>
          <TableComponent.TableRow>
            <TableComponent.TableHeaderCell aria-label="Chevron"></TableComponent.TableHeaderCell>
            <TableComponent.TableHeaderCell>
              Provider
            </TableComponent.TableHeaderCell>
            <TableComponent.TableHeaderCell>
              Integration
            </TableComponent.TableHeaderCell>
            <TableComponent.TableHeaderCell>
              Installation ID
            </TableComponent.TableHeaderCell>
            <TableComponent.TableHeaderCell>
              Last Operation Status
            </TableComponent.TableHeaderCell>
            <TableComponent.TableHeaderCell>
              Created
            </TableComponent.TableHeaderCell>
            <TableComponent.TableHeaderCell aria-label="Actions"></TableComponent.TableHeaderCell>
          </TableComponent.TableRow>
        </TableComponent.TableHeader>
        <TableComponent.TableBody>
          {customerInstallations.map((installation) => {
            const integration = integrations?.find(
              (i) => i.id === installation.integrationId
            );
            const providerName = integration?.provider;

            const providerDisplayName =
              providers?.[providerName || ""]?.displayName ||
              capitalizeFirstLetter(providerName || "");

            return (
              <InstallationRow
                key={installation.id}
                installation={installation}
                integration={integration}
                providerDisplayName={providerDisplayName}
                projectId={projectId}
                customerName={customerName}
              />
            );
          })}
        </TableComponent.TableBody>
      </TableComponent.Table>
      <TableComponent.TableFooterDiv>
        Total Installations: {customerInstallations.length}
      </TableComponent.TableFooterDiv>
    </>
  );
};

export const CustomerDetailsPage = () => {
  const { projectId, groupRef } = useParams();
  const {
    data: installations,
    isLoading: isLoadingInstallations,
    isError: isErrorInstallations,
  } = useListAllInstallations();
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useListIntegrations();
  const { data: providers } = useProviders();

  const customerInstallations =
    installations?.filter(
      (installation) => installation.group?.groupRef === groupRef
    ) || [];

  const customerName = customerInstallations[0]?.group?.groupName || groupRef;

  const isLoading = isLoadingInstallations || isLoadingIntegrations;
  const isError = isErrorInstallations;

  // Create breadcrumb links
  const links = [
    {
      text: "Customers",
      href: `/projects/${projectId}/customers`,
    },
  ];

  if (!projectId) {
    return <div>No project ID found</div>;
  }

  return (
    <MainPageTemplate title={`Customer: ${customerName}`} links={links}>
      {isError && (
        <p className="p-4">There was an error loading the customer details.</p>
      )}
      {isLoading && <LoadingPage variant="table" />}
      {!isLoading && !isError && (
        <CustomerDetailsContent
          customerInstallations={customerInstallations}
          integrations={integrations}
          providers={providers}
          projectId={projectId}
        />
      )}
    </MainPageTemplate>
  );
};
